import { z } from 'zod';
import { PERSON_GENDERS } from '../utils/mechitza.js';
const CAMPAIGN_STATUSES = ['DRAFT', 'SCHEDULED', 'SENDING', 'SENT', 'CLOSED', 'ARCHIVED'];
const CHANNELS = ['EMAIL', 'SMS', 'WHATSAPP', 'LINK'];
/** #RGB or #RRGGBB hex color. */
const hexColorSchema = z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Invalid hex color');
const campaignSettingsSchema = z
    .object({
    allowMaybe: z.boolean().optional(),
    allowPartyEdit: z.boolean().optional(),
    showHebrewDate: z.boolean().optional(),
    showSeatFinder: z.boolean().optional(),
    backgroundColor: hexColorSchema.optional(),
    accentColor: hexColorSchema.optional(),
    includeEventLogo: z.boolean().optional(),
    showTimeline: z.boolean().optional(),
    showEnvelope: z.boolean().optional(),
    envelopeSealLogo: z.boolean().optional(),
    envelopeColor: hexColorSchema.optional(),
    sealColor: hexColorSchema.optional(),
    envelopeMessage: z.string().max(200).optional(),
    rsvpEnabled: z.boolean().optional(),
    allowPublicRsvp: z.boolean().optional(),
})
    .strict();
export const createInviteDesignSchema = z.object({
    eventId: z.string().uuid().optional(),
    name: z.string().min(1).max(255),
    designType: z.enum(['UPLOAD', 'CANVAS']).optional(),
    isTemplate: z.boolean().optional(),
    templateName: z.string().max(255).optional(),
    config: z.record(z.unknown()).optional(),
});
export const updateInviteDesignSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    templateName: z.string().max(255).nullable().optional(),
    s3Key: z.string().max(1024).nullable().optional(),
    mimeType: z.string().max(255).nullable().optional(),
    config: z.record(z.unknown()).optional(),
});
/**
 * Artwork is always an image: a PDF is turned into one image a page in the
 * browser before it is uploaded, so the guest's page and the email only ever
 * show pictures.
 */
export const INVITE_ARTWORK_CONTENT_TYPES = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
];
export const inviteArtworkUploadUrlSchema = z.object({
    fileName: z.string().min(1).max(255),
    contentType: z.enum(INVITE_ARTWORK_CONTENT_TYPES, {
        errorMap: () => ({ message: 'Artwork must be a PNG, JPEG, WebP or GIF image' }),
    }),
});
export const createInviteDesignPageSchema = z.object({
    s3Key: z.string().min(1).max(1024),
    mimeType: z.string().max(255).nullable().optional(),
    sortOrder: z.number().int().min(0).max(100).optional(),
});
export const setCampaignPublicLinkSchema = z.object({
    enabled: z.boolean(),
});
export const createInviteCampaignSchema = z.object({
    eventId: z.string().uuid(),
    name: z.string().min(1).max(255),
    designId: z.string().uuid().nullable().optional(),
    subject: z.string().max(255).optional(),
    message: z.string().max(10000).optional(),
    rsvpDeadline: z.string().datetime().nullable().optional(),
    rsvpEventIds: z.array(z.string().uuid()).max(20).optional(),
    timelineId: z.string().uuid().nullable().optional(),
    settings: campaignSettingsSchema.optional(),
});
export const updateInviteCampaignSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    designId: z.string().uuid().nullable().optional(),
    subject: z.string().max(255).nullable().optional(),
    message: z.string().max(10000).nullable().optional(),
    rsvpDeadline: z.string().datetime().nullable().optional(),
    rsvpEventIds: z.array(z.string().uuid()).max(20).optional(),
    timelineId: z.string().uuid().nullable().optional(),
    settings: campaignSettingsSchema.optional(),
    status: z.enum(CAMPAIGN_STATUSES).optional(),
});
const campaignRecipientFilterSchema = z
    .object({
    sides: z.array(z.string().min(1).max(100)).max(50).optional(),
    tags: z.array(z.string().min(1).max(100)).max(50).optional(),
    responses: z
        .array(z.enum(['PENDING', 'YES', 'NO', 'MAYBE']))
        .max(4)
        .optional(),
    notInvited: z.boolean().optional(),
})
    .strict();
export const addCampaignRecipientsSchema = z
    .object({
    guestIds: z.array(z.string().uuid()).min(1).max(1000).optional(),
    allGuests: z.boolean().optional(),
    filter: campaignRecipientFilterSchema.optional(),
    dryRun: z.boolean().optional(),
    channel: z.enum(CHANNELS).optional(),
})
    .refine((input) => input.allGuests === true ||
    (input.guestIds?.length ?? 0) > 0 ||
    // A filter must narrow by something: an empty one would add everyone.
    !!(input.filter?.sides?.length ||
        input.filter?.tags?.length ||
        input.filter?.responses?.length ||
        input.filter?.notInvited), { message: 'Provide guestIds, a filter that names who, or set allGuests', path: ['guestIds'] });
/** Remind the non-responders — or only the ones picked. */
export const remindCampaignSchema = z.object({
    recipientIds: z.array(z.string().uuid()).min(1).max(1000).optional(),
});
const RECIPIENT_STATUSES = [
    'PENDING',
    'QUEUED',
    'SENT',
    'DELIVERED',
    'OPENED',
    'RESPONDED',
    'BOUNCED',
    'FAILED',
];
/**
 * Producer override of a recipient's RSVP and/or delivery status — at least
 * one of the two must be present, or there is nothing to change.
 */
export const updateCampaignRecipientSchema = z
    .object({
    response: z.enum(['PENDING', 'YES', 'NO', 'MAYBE']).optional(),
    adultCount: z.number().int().min(0).max(100).optional(),
    kidCount: z.number().int().min(0).max(100).optional(),
    status: z.enum(RECIPIENT_STATUSES).optional(),
    /** Who of the named party is coming to the main event. */
    members: z
        .array(z.object({ id: z.string().uuid(), attending: z.boolean().nullable() }))
        .max(100)
        .optional(),
})
    .refine((input) => input.response !== undefined || input.status !== undefined, {
    message: 'Provide a response or a status',
    path: ['response'],
})
    .refine((input) => input.members === undefined || input.response === 'YES' || input.response === 'MAYBE', { message: 'Who is coming goes with a coming answer', path: ['members'] });
// ─── Meal options / party members / questions (Phase 2) ─────────────────────
export const createEventMealOptionSchema = z.object({
    name: z.string().min(1).max(255),
    description: z.string().max(1000).optional(),
    isKidsMeal: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
export const updateEventMealOptionSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(1000).nullable().optional(),
    isKidsMeal: z.boolean().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
});
const MEMBER_TYPES = ['ADULT', 'CHILD'];
export const createPartyMemberSchema = z.object({
    name: z.string().min(1).max(255),
    memberType: z.enum(MEMBER_TYPES).optional(),
    gender: z.enum(PERSON_GENDERS).nullable().optional(),
    email: z.string().email().max(255).nullable().optional(),
    phone: z.string().max(50).nullable().optional(),
    isPrimary: z.boolean().optional(),
    mealOptionId: z.string().uuid().nullable().optional(),
    dietaryNotes: z.string().max(1000).optional(),
    sortOrder: z.number().int().optional(),
});
export const updatePartyMemberSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    memberType: z.enum(MEMBER_TYPES).optional(),
    gender: z.enum(PERSON_GENDERS).nullable().optional(),
    email: z.string().email().max(255).nullable().optional(),
    phone: z.string().max(50).nullable().optional(),
    isPrimary: z.boolean().optional(),
    mealOptionId: z.string().uuid().nullable().optional(),
    dietaryNotes: z.string().max(1000).nullable().optional(),
    sortOrder: z.number().int().optional(),
});
const RSVP_QUESTION_TYPES = ['TEXT', 'YES_NO', 'SINGLE_CHOICE', 'NUMBER'];
const rsvpQuestionConfigSchema = z
    .object({
    choices: z.array(z.string().min(1).max(255)).max(50).optional(),
    placeholder: z.string().max(255).optional(),
})
    .strict();
export const createRsvpQuestionSchema = z.object({
    prompt: z.string().min(1).max(1000),
    questionType: z.enum(RSVP_QUESTION_TYPES),
    isRequired: z.boolean().optional(),
    config: rsvpQuestionConfigSchema.optional(),
    sortOrder: z.number().int().optional(),
});
export const updateRsvpQuestionSchema = z.object({
    prompt: z.string().min(1).max(1000).optional(),
    questionType: z.enum(RSVP_QUESTION_TYPES).optional(),
    isRequired: z.boolean().optional(),
    config: rsvpQuestionConfigSchema.optional(),
    sortOrder: z.number().int().optional(),
});
// ─── Public respond ─────────────────────────────────────────────────────────
const RSVP_COUNT = z.number().int().min(0).max(100);
const RSVP_RESPONSES = ['YES', 'NO', 'MAYBE'];
const rsvpAnswerValueSchema = z
    .object({
    text: z.string().max(2000).optional(),
    number: z.number().optional(),
    bool: z.boolean().optional(),
    choice: z.string().max(255).optional(),
})
    .strict();
export const publicRsvpRespondSchema = z
    .object({
    response: z.enum(RSVP_RESPONSES).optional(),
    adultCount: RSVP_COUNT.optional(),
    kidCount: RSVP_COUNT.optional(),
    notes: z.string().max(2000).optional(),
    responses: z
        .array(z.object({
        eventId: z.string().uuid(),
        response: z.enum(RSVP_RESPONSES),
        adultCount: RSVP_COUNT.optional(),
        kidCount: RSVP_COUNT.optional(),
    }))
        .min(1)
        .max(20)
        .optional(),
    partyMembers: z
        .array(z.object({
        id: z.string().uuid().optional(),
        name: z.string().min(1).max(255),
        memberType: z.enum(MEMBER_TYPES).optional(),
        email: z
            .union([z.string().email().max(255), z.literal(''), z.null()])
            .optional()
            .transform((value) => (value === '' ? null : value)),
        mealOptionId: z.string().uuid().nullable().optional(),
        dietaryNotes: z.string().max(1000).optional(),
        /** Coming to the main event; null is not answered. */
        attending: z.boolean().nullable().optional(),
    }))
        .max(100)
        .optional(),
    answers: z
        .array(z.object({ questionId: z.string().uuid(), value: rsvpAnswerValueSchema }))
        .max(100)
        .optional(),
})
    .refine((input) => input.response !== undefined || (input.responses?.length ?? 0) > 0, {
    message: 'Provide a response or a responses array',
    path: ['response'],
});
/**
 * Self-identified RSVP from the guest-less share link. Email is required so
 * the confirmation and the responder's personal invitation link have somewhere
 * to go — it is never treated as proof of identity (see `selfRsvpPublicInvite`).
 */
export const publicSelfRsvpSchema = z.object({
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().max(100).optional(),
    email: z.string().trim().email().max(255),
    phone: z.string().trim().max(50).optional(),
    response: z.enum(RSVP_RESPONSES),
    adultCount: RSVP_COUNT.optional(),
    kidCount: RSVP_COUNT.optional(),
    notes: z.string().max(2000).optional(),
});
// ─── Guest messages / scheduling (Phase 3) ──────────────────────────────────
const guestMessageAudienceSchema = z
    .object({
    rsvpStatuses: z
        .array(z.enum(['invited', 'confirmed', 'declined', 'maybe']))
        .max(4)
        .optional(),
    sides: z
        .array(z.enum(['bride', 'groom', 'mutual', 'other']))
        .max(4)
        .optional(),
})
    .strict();
/**
 * Broadcast channels a producer can pick. WHATSAPP/LINK exist on the schema
 * enum for recipients but have no send implementation, so they are not
 * offerable here.
 */
const MESSAGE_CHANNELS = ['EMAIL', 'SMS'];
export const createGuestMessageSchema = z.object({
    eventId: z.string().uuid(),
    channel: z.enum(MESSAGE_CHANNELS).optional(),
    subject: z.string().max(255).optional(),
    body: z.string().min(1).max(10000),
    audience: guestMessageAudienceSchema.optional(),
});
export const updateGuestMessageSchema = z.object({
    channel: z.enum(MESSAGE_CHANNELS).optional(),
    subject: z.string().max(255).nullable().optional(),
    body: z.string().min(1).max(10000).optional(),
    audience: guestMessageAudienceSchema.optional(),
});
export const scheduleCampaignSchema = z.object({
    scheduledAt: z.string().datetime().nullable(),
});
export const checkInSchema = z.object({
    undo: z.boolean().optional(),
    /** Event the operator is checking in for — a token from another event is rejected. */
    eventId: z.string().uuid().optional(),
    /** How many of the party have arrived; absent = the whole party (or the named members sent). */
    arrived: z.number().int().min(0).max(500).optional(),
    /** The named members who have arrived — the rest of the named party have not. */
    memberIds: z.array(z.string().uuid()).max(100).optional(),
    /** When it happened, for a check-in queued offline and replayed later. */
    at: z.string().datetime().optional(),
    /** Apply only if the party still stands so — an undo, or an edit replayed from offline. */
    ifState: z
        .object({
        arrivedCount: z.number().int().min(0).max(500),
        memberIds: z.array(z.string().uuid()).max(100),
    })
        .optional(),
});
//# sourceMappingURL=invite.js.map